export type CmsBadgeAsset = {
  available: boolean;
  value: string;
  status?: number;
};

const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

class CMSBadgeAssetCache {
  private assets = new Map<string, CmsBadgeAsset>();
  private requests = new Map<string, Promise<CmsBadgeAsset>>();

  get = (url: string) => this.assets.get(url);

  getValue = async (url: string): Promise<string> => {
    const asset = this.assets.get(url) || (await this.requests.get(url));
    return asset?.value || url;
  };

  clear = (urls: string[]) => {
    urls.forEach(url => {
      if (isOffline() && this.assets.has(url)) return;
      this.assets.delete(url);
      this.requests.delete(url);
    });
  };

  load = (url: string): Promise<CmsBadgeAsset> => {
    const cached = this.assets.get(url);
    if (cached) return Promise.resolve(cached);
    const pending = this.requests.get(url);
    if (pending) return pending;

    const request = this.download(url);
    this.requests.set(url, request);
    return request.then(asset => {
      if (this.requests.get(url) === request) {
        if (asset.available || asset.status === 404) this.assets.set(url, asset);
        this.requests.delete(url);
      }
      return asset;
    });
  };

  private download = (url: string): Promise<CmsBadgeAsset> => {
    return new Promise(resolve => {
      const unavailable = (status?: number) => resolve({ available: false, value: "", status });
      try {
        const xhr = new XMLHttpRequest();
        xhr.onload = () => {
          if (xhr.status < 200 || xhr.status >= 300) {
            unavailable(xhr.status);
            return;
          }
          const resolveWithUrl = () => resolve({ available: true, value: url });
          if (!xhr.response || typeof FileReader === "undefined") {
            resolveWithUrl();
            return;
          }
          try {
            const reader = new FileReader();
            reader.onloadend = () => {
              const value = typeof reader.result === "string" && reader.result.startsWith("data:") ? reader.result : url;
              resolve({ available: true, value });
            };
            reader.onerror = resolveWithUrl;
            reader.onabort = resolveWithUrl;
            reader.readAsDataURL(xhr.response);
          } catch {
            resolveWithUrl();
          }
        };
        xhr.onerror = () => unavailable();
        xhr.onabort = () => unavailable();
        xhr.ontimeout = () => unavailable();
        xhr.open("GET", url);
        xhr.responseType = "blob";
        xhr.send();
      } catch {
        unavailable();
      }
    });
  };
}

const cmsBadgeAssetCache = new CMSBadgeAssetCache();

export default cmsBadgeAssetCache;
